import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

/**
 * **Out of the column and onto the page** — `dashboard/src/components/ui/
 * portal.tsx`, copied here with the modal it exists for.
 *
 * A scrim that says it covers the app has to be a child of `<body>`, not of
 * whatever the reader happened to be looking at when they opened it.
 * `position: fixed` measures against the viewport, so the scrim is always the
 * right *size*; what it is not free of is where it is *painted*. An ancestor
 * that opens a stacking context takes every descendant's `z-index` with it, and
 * the whole subtree is then stacked as one unit at the ancestor's own level.
 * This app's nav rail is `z-index: 2` on the root context, and 2 beats a subtree
 * at 0 — so a scrim mounted inside the column would darken the column and stop
 * dead at the rail, with the rail's links still clickable behind an
 * `aria-modal="true"` dialog.
 *
 * Raising the scrim's own `z-index` cannot fix that — no number inside a
 * stacking context escapes it. Moving the node does, and moving it to `body`
 * means no ancestor between the scrim and the root can ever do this again.
 *
 * **The fallback is not a nicety.** Every spec in this package renders through
 * `renderToStaticMarkup` in vitest's node environment, where there is no
 * document to portal into and where `createPortal` throws. With no DOM there is
 * also no stacking to get wrong, so rendering the children where they stand is
 * both the only thing available and the right answer: it is what lets the copy
 * sweep see an open modal at all.
 */
export function Portal({ children }: { children: ReactNode }) {
  if (typeof document === 'undefined') return <>{children}</>;
  return createPortal(children, document.body);
}
